import React, { useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { TrashIcon } from "@heroicons/react/24/outline";
import Input from "./ui/Input";
import Select from "./ui/Select";
import Button from "./ui/Button";
import {
  SKI_LEVELS,
  type Participant,
  SNOWBOARD_LEVELS,
} from "../../types/booking";
import { useGetServiceListQuery } from "../../services/common.service";
import { CURRENCY_SYMBOL } from "../../constants/constant";
import { formatDateLocal } from "../../utils/helpers";

type ServiceItem = {
  id: number;
  description: string;
  lesson_type?: string;
  service_mode?: string;
  cost?: number | string;
  periodStart?: string;
  periodEnd?: string;
};

const normalizeItems = (arr: unknown[]): ServiceItem[] => {
  return arr.map((raw) => {
    const item = raw as Record<string, unknown>;
    const rawCost = item?.cost ?? item?.price ?? item?.rate;
    return {
      id: Number(item?.id),
      description: String(item?.description ?? item?.name ?? ""),
      lesson_type: (item?.lesson_type ?? item?.type) as string | undefined,
      service_mode: (item?.service_mode ?? item?.category) as
        | string
        | undefined,
      cost:
        rawCost !== undefined && rawCost !== null
          ? (rawCost as number | string)
          : undefined,
      periodStart: (item?.period_start ?? item?.periodStart) as
        | string
        | undefined,
      periodEnd: (item?.period_end ?? item?.periodEnd) as string | undefined,
    };
  });
};

const withCostLabel = (service: ServiceItem): string => {
  const numericCost =
    service.cost === undefined || service.cost === null
      ? NaN
      : Number(service.cost);

  if (!Number.isNaN(numericCost)) {
    const mode = service.service_mode?.toLowerCase();
    const suffix =
      mode === "groups"
        ? " per person"
        : mode === "private"
          ? " per instructor"
          : "";
    return `${service.description} (Price: ${CURRENCY_SYMBOL}${numericCost}${suffix})`;
  }

  return service.description;
};

interface ParticipantErrors {
  name?: { message?: string };
  age?: { message?: string };
  activity?: { message?: string };
  timeSlot?: { message?: string };
  skillLevel?: { message?: string };
  lessonType?: { message?: string };
}

interface ParticipantRowProps {
  participant: Participant;
  onUpdate: (updates: Partial<Participant>) => void;
  onRemove: () => void;
  canRemove: boolean;
  index: number;
  errors?: ParticipantErrors;
  lessonDate: Date | string | null;
}

const ParticipantRow: React.FC<ParticipantRowProps> = ({
  participant,
  onUpdate,
  onRemove,
  canRemove,
  index,
  errors,
  lessonDate,
}) => {
  const normalizedLessonDate = useMemo(() => {
    if (!lessonDate) {
      return null;
    }
    if (lessonDate instanceof Date) {
      return formatDateLocal(lessonDate);
    }
    return lessonDate;
  }, [lessonDate]);

  const serviceQuery = useMemo(() => {
    if (!normalizedLessonDate) {
      return undefined;
    }

    let query = `pagination=false&online_booking=Y&status=Active&booking_date=${normalizedLessonDate}`;
    if (participant.age && participant.age >= 3) {
      query += `&age=${participant.age}`;
    }
    if (participant.timeSlot) {
      query += `&lesson_code_type=${participant.timeSlot}`;
    }

    return query;
  }, [normalizedLessonDate, participant.age, participant.timeSlot]);

  const { data: servicesData, isFetching } =
    useGetServiceListQuery(serviceQuery);

  const services = useMemo(() => {
    const items =
      ((servicesData as Record<string, unknown>)?.items as unknown[]) ?? [];
    return normalizeItems(items);
  }, [servicesData]);

  const privateItems = useMemo(
    () => services.filter((s) => s.service_mode?.toLowerCase() === "private"),
    [services],
  );

  const groupItems = useMemo(
    () => services.filter((s) => s.service_mode?.toLowerCase() === "groups"),
    [services],
  );

  const otherItems = useMemo(
    () =>
      services.filter((s) => {
        const mode = s.service_mode?.toLowerCase();
        return !s.service_mode || (mode !== "groups" && mode !== "private");
      }),
    [services],
  );

  const privateOptions = useMemo(
    () =>
      privateItems.map((s) => ({
        value: String(s.id),
        label: withCostLabel(s),
      })),
    [privateItems],
  );

  const groupOptions = useMemo(
    () =>
      groupItems.map((s) => ({
        value: String(s.id),
        label: withCostLabel(s),
      })),
    [groupItems],
  );

  const otherOptions = useMemo(
    () =>
      otherItems.map((s) => ({
        value: String(s.id),
        label: withCostLabel(s),
      })),
    [otherItems],
  );

  const baseLessonGroups = useMemo(() => {
    const groups: {
      heading: string;
      options: { value: string; label: string }[];
    }[] = [];
    if (groupOptions.length) {
      groups.push({ heading: "Group list", options: groupOptions });
    }
    if (privateOptions.length) {
      groups.push({ heading: "Private list", options: privateOptions });
    }
    if (otherOptions.length) {
      groups.push({ heading: "Other list", options: otherOptions });
    }
    return groups;
  }, [groupOptions, privateOptions, otherOptions]);

  const isPrivateOnlyAge = participant.age === 3 || participant.age === 4;
  const lessonTypeOptions = useMemo(() => {
    if (isPrivateOnlyAge) {
      return privateOptions;
    }
    return [...groupOptions, ...privateOptions, ...otherOptions];
  }, [isPrivateOnlyAge, groupOptions, privateOptions, otherOptions]);

  const lessonTypeGroups = useMemo(() => {
    if (isPrivateOnlyAge) {
      return privateOptions.length
        ? [{ heading: "Private list", options: privateOptions }]
        : [];
    }
    return baseLessonGroups;
  }, [isPrivateOnlyAge, baseLessonGroups, privateOptions]);

  const allServices = services;

  // Clear lesson type when available options change, or age/date changes
  useEffect(() => {
    if (
      !isFetching &&
      participant.lessonType &&
      !lessonTypeOptions.some(
        (option) => option.value === participant.lessonType,
      )
    ) {
      onUpdate({ lessonType: "", lessonTypeAvailability: undefined });
    }
  }, [lessonTypeOptions, onUpdate, participant.lessonType, isFetching]);

  // Reset lesson type when lesson date or age filtering criteria change
  const criteriaRef = useRef<{
    lessonDate: string | null;
    age?: number;
    timeSlot?: string;
  }>({ lessonDate: normalizedLessonDate, age: participant.age, timeSlot: participant.timeSlot });

  useEffect(() => {
    const previous = criteriaRef.current;
    const lessonDateChanged = previous.lessonDate !== normalizedLessonDate;
    const ageChanged = previous.age !== participant.age;
    const timeSlotChanged = previous.timeSlot !== participant.timeSlot;

    if ((lessonDateChanged || ageChanged || timeSlotChanged) && participant.lessonType) {
      onUpdate({ lessonType: "", lessonTypeAvailability: undefined });
    }

    criteriaRef.current = {
      lessonDate: normalizedLessonDate,
      age: participant.age,
      timeSlot: participant.timeSlot,
    };
  }, [normalizedLessonDate, participant.age, participant.timeSlot, participant.lessonType, onUpdate]);

  const handleLessonTypeChange = (lessonTypeId: string) => {
    const selectedService = allServices.find(
      (service) => String(service.id) === String(lessonTypeId),
    );

    onUpdate({
      lessonType: lessonTypeId,
      lessonTypeAvailability:
        selectedService?.periodStart && selectedService?.periodEnd
          ? {
              periodStart: selectedService.periodStart,
              periodEnd: selectedService.periodEnd,
            }
          : undefined,
    });
  };

  const skillLevelOptions = () => {
    const levels =
      participant.activity === "Ski" ? SKI_LEVELS : SNOWBOARD_LEVELS;
    return levels.map((level) => ({ value: level, label: level }));
  };

  const timeSlotOptions = [
    { value: "AM", label: "AM" },
    { value: "PM", label: "PM" },
    { value: "FD", label: "FD (Full Day)" },
    // { value: "AMPM", label: "Both AM and PM" },
  ];

  const activityOptions = [
    { value: "Ski", label: "Ski" },
    { value: "Snowboard", label: "Snowboard" },
  ];

  const isLessonTypeDisabled =
    isFetching ||
    !normalizedLessonDate ||
    (participant.age !== undefined && participant.age < 3);

  const lessonTypePlaceholder = isFetching
    ? "Loading lesson types..."
    : !normalizedLessonDate
      ? "Select the lesson date first"
      : "Select lesson type";

  const selectedService = useMemo(() => {
    return allServices.find(
      (service) => String(service.id) === String(participant.lessonType),
    );
  }, [allServices, participant.lessonType]);

  const levelMismatchError = useMemo(() => {
    if (
      selectedService?.service_mode?.toLowerCase() === "groups" &&
      participant.skillLevel === "SK6"
    ) {
      return "Level 6 is not selectable for group lessons";
    }
    return undefined;
  }, [selectedService, participant.skillLevel]);

  // Propagate the mismatch to the form state for cross-component validation
  useEffect(() => {
    const isMismatch = !!levelMismatchError;
    if (participant.hasLevelMismatch !== isMismatch) {
      onUpdate({ hasLevelMismatch: isMismatch });
    }
  }, [levelMismatchError, participant.hasLevelMismatch, onUpdate]);

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
      className="bg-gray-50 p-4 rounded-lg border"
    >
      <div className="flex justify-between items-center mb-4">
        <h4 className="font-medium text-gray-900">Participant {index + 1}</h4>
        {canRemove && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <div className="xl:col-span-2">
          <Input
            label="Name"
            placeholder="Participant name"
            required
            value={participant.name}
            onChange={(e) => onUpdate({ name: e.target.value })}
            error={errors?.name?.message}
          />
        </div>

        <div>
          <Select
            label="Age (if 12 or under)"
            options={[
              { value: "", label: "Select age" },
              ...Array.from({ length: 10 }, (_, i) => ({
                value: String(i + 3),
                label: String(i + 3),
              })),
            ]}
            value={participant.age?.toString() || ""}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "") {
                onUpdate({ age: undefined });
              } else {
                onUpdate({ age: parseInt(value, 10) });
              }
            }}
            error={errors?.age?.message}
          />
        </div>

        <div>
          <Select
            label="Ski or Snowboard"
            placeholder="Select"
            required
            options={activityOptions}
            value={participant.activity}
            onChange={(e) => {
              const activity = e.target.value as "Ski" | "Snowboard" | "";
              onUpdate({ activity, skillLevel: "" }); // Reset skill level when activity changes
            }}
            error={errors?.activity?.message}
          />
        </div>

        <div>
          <Select
            label="AM, PM or FD"
            required
            options={timeSlotOptions}
            value={participant.timeSlot}
            onChange={(e) =>
              onUpdate({ timeSlot: e.target.value as "AM" | "PM" | "FD" })
            }
            error={errors?.timeSlot?.message}
          />
        </div>

        <div>
          <Select
            label="Level of Ability"
            placeholder="Select"
            required
            options={skillLevelOptions()}
            value={participant.skillLevel}
            onChange={(e) => onUpdate({ skillLevel: e.target.value })}
            disabled={!participant.activity}
            error={levelMismatchError || errors?.skillLevel?.message}
          />
        </div>
      </div>

      <div className="mt-4">
        <Select
          label="Lesson Type"
          id="age-dropdown"
          placeholder={lessonTypePlaceholder}
          required
          options={lessonTypeOptions}
          groups={lessonTypeGroups}
          isSearchable
          searchPlaceholder="Search lesson types..."
          value={participant.lessonType}
          onChange={(e) => handleLessonTypeChange(e.target.value)}
          disabled={isLessonTypeDisabled}
          error={levelMismatchError || errors?.lessonType?.message}
        />
      </div>
    </motion.div>
  );
};

export default ParticipantRow;
